<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col cols="12" md="10">
                    <b-link :to="{ name: 'settings' }">
                        <font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                    <h1 class="mt-1 mb-2">
                        {{ $t("mainmenu.settings") }} /
                        {{ $t("settings.teams.Teams") }}
                    </h1>

                    <div v-if="!isLoading" class="mb-2">
                        <b-row align-h="between">
                            <b-col class="d-flex" cols="12" md="6">
                                <b-button
                                    v-if="$can('create', 'teams')"
                                    id="contacts-add"
                                    :to="{ name: 'settings-teams-add' }"
                                    class="mr-2 text-nowrap"
                                    variant="primary"
                                >
                                    <font-awesome-icon class="mr-50" icon="plus" />
                                    <span>{{ $t("settings.teams.Add team") }}</span>
                                </b-button>

                                <div class="d-flex align-items-center justify-content-end">
                                    <b-form-input
                                        v-model="searchQuery"
                                        :placeholder="$t('paginator.search')"
                                        class="d-inline-block mr-1 bg-light"
                                        debounce="500"
                                    />
                                </div>
                            </b-col>

                            <b-col cols="auto">
                                <label>{{ $t("paginator.show") }}</label>
                                <v-select
                                    v-model="perPage"
                                    :clearable="false"
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    :options="perPageOptions"
                                    class="per-page-selector d-inline-block mx-50"
                                />
                                <label>{{ $t("paginator.entries") }}</label>
                            </b-col>
                        </b-row>
                    </div>

                    <b-table
                        ref="refTable"
                        :empty-text="$t('paginator.no-matching-records-found')"
                        :fields="tableColumns"
                        :items="localRead"
                        :no-sort-reset="true"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="isSortDirDesc"
                        :table-busy="isLoading"
                        class="position-relative"
                        primary-key="id"
                        responsive
                        show-empty
                    >
                        <template #table-busy>
                            <div class="d-flex justify-content-center my-1">
                                <b-spinner />
                            </div>
                        </template>

                        <!-- Column: Name -->
                        <template #cell(name)="data">
                            <strong>{{ data.item.name }}</strong>
                        </template>

                        <!-- Column: channels -->
                        <template #cell(channels)="data">
                            <font-awesome-icon
                                v-for="channel in data.item.channels"
                                :id="`channel-${channel.id}-team-${data.item.id}-icon`"
                                :key="`channel-${channel.id}-team-${data.item.id}-icon`"
                                v-b-tooltip="channel.name"
                                :icon="resolvePlatformIcon(channel.provider, channel.platform)"
                                class="team-channel-icon text-secondary mr-50"
                                size="lg"
                            />
                        </template>

                        <!-- Column: users -->
                        <template #cell(teamUsers)="data">
                            <font-awesome-icon
                                v-b-tooltip="
                                    getUsersAssignedToTeam(data.item.teamUsers)
                                        .map((user) => user.name)
                                        .join(', ')
                                "
                                :icon="['far', 'circle-ellipsis']"
                            />
                            {{ data.item.teamUsers.length }}
                        </template>

                        <!-- Column: color -->
                        <template #cell(color)="data">
                            <b-badge
                                :style="{
                                    'background-color': data.item.color,
                                    color: textColorByBackgroundColor(data.item.color),
                                }"
                                >{{ data.item.color.toUpperCase() }}
                            </b-badge>
                        </template>

                        <!-- Column: Actions -->
                        <template #cell(actions)="data">
                            <div class="text-right">
                                <b-button
                                    :to="{
                                        name: 'settings-teams-edit',
                                        params: { id: data.item.id },
                                    }"
                                    class="btn-icon mr-50"
                                    variant="light text-secondary"
                                >
                                    <font-awesome-icon icon="pen" />
                                </b-button>
                                <b-button
                                    class="btn-icon"
                                    variant="light text-secondary"
                                    @click="deleteTeam(data.item.id, data.item.name)"
                                >
                                    <font-awesome-icon icon="trash" />
                                </b-button>
                            </div>
                        </template>
                    </b-table>

                    <div v-if="!isLoading" class="mx-2 mb-2">
                        <b-row>
                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-start"
                                cols="12"
                                sm="6"
                            >
                                <span class="text-muted">
                                    {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }}
                                    {{ dataMeta.of }} {{ $t("paginator.entries") }}
                                </span>
                            </b-col>

                            <b-col
                                class="d-flex align-items-center justify-content-center justify-content-sm-end"
                                cols="12"
                                sm="6"
                            >
                                <b-pagination
                                    v-model="currentPage"
                                    :per-page="perPage"
                                    :total-rows="total"
                                    class="mb-0 mt-1 mt-sm-0"
                                    first-number
                                    last-number
                                    next-class="next-item"
                                    prev-class="prev-item"
                                    size="sm"
                                >
                                    <template #prev-text>
                                        <font-awesome-icon icon="chevron-left" size="lg" />
                                    </template>
                                    <template #next-text>
                                        <font-awesome-icon icon="chevron-right" size="lg" />
                                    </template>
                                </b-pagination>
                            </b-col>
                        </b-row>
                    </div>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BCard,
    BCardText,
    BCol,
    BContainer,
    BDropdown,
    BDropdownItem,
    BFormInput,
    BLink,
    BPagination,
    BRow,
    BSpinner,
    BTab,
    BTable,
    BTabs,
    VBTooltip,
} from "bootstrap-vue";
import axios from "@/libs/axios";
import useTeamsList from "./useTeamsList";
import vSelect from "vue-select";
import { resolvePlatformIcon } from "@/utils/channels";
import { textColorByBackgroundColor } from "@/utils/color";
import i18n from "@/libs/i18n";
import { useToast } from "vue-toastification/composition";
import toastificationContent from "@/layouts/components/toastification/toastificationContent";

export default {
    components: {
        BContainer,
        BCard,
        BBadge,
        BRow,
        BCol,
        BFormInput,
        BButton,
        BTable,
        BDropdown,
        BDropdownItem,
        BPagination,
        BCardText,
        BTabs,
        BTab,
        BLink,
        BSpinner,
        vSelect,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    setup() {
        const toast = useToast();
        const {
            read,
            refetchData,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            getUsersAssignedToTeam,
            isLoading,
        } = useTeamsList();

        const localRead = (ctx, callback) => {
            read(ctx, callback).catch(() => toast.error(i18n.t("settings.teams.error-reading")));
        };

        return {
            localRead,
            refetchData,
            tableColumns,
            perPage,
            currentPage,
            total,
            dataMeta,
            perPageOptions,
            searchQuery,
            sortBy,
            isSortDirDesc,
            refTable,
            getUsersAssignedToTeam,
            resolvePlatformIcon,
            textColorByBackgroundColor,
            toast,
            isLoading,
        };
    },
    methods: {
        deleteTeam(teamId, name) {
            this.$bvModal
                .msgBoxConfirm(this.$t("confirm.Are you sure?"), {
                    title: this.$t("confirm.Are you sure?"),
                    cancelTitle: this.$t("confirm.Cancel"),
                    cancelVariant: "light",
                    okTitle: this.$t("confirm.Yes, delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                    bodyClass: "mx-auto",
                    headerClass: "text-center",
                })
                .then((result) => {
                    if (result) {
                        axios
                            .delete(`teams/${teamId}`)
                            .then(() => {
                                this.refetchData();

                                this.toast({
                                    component: toastificationContent,
                                    props: {
                                        title: i18n.t("settings.teams.Delete team"),
                                        text: i18n.t("toast.delete.success", { entity: i18n.t("settings.teams.Team") }),
                                        icon: "check",
                                        variant: "success",
                                    },
                                });
                            })
                            .catch(() => {
                                this.toast({
                                    component: toastificationContent,
                                    props: {
                                        title: i18n.t("settings.teams.Delete team"),
                                        text: i18n.t("toast.edlete.error", { entity: i18n.t("settings.teams.Team") }),
                                        icon: "exclamation-triangle",
                                        variant: "warning",
                                    },
                                });
                            });
                    }
                });
        },
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
</style>
